/**
 * Typed RPC helper functions for Supabase calls
 * These wrap RPC calls that aren't in the auto-generated types
 */

import { supabase } from "@/integrations/supabase/client";
import type { PostgrestSingleResponse, PostgrestError } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

// Type definitions for RPC parameters and responses
export interface InitAccountParams {
  p_role: string;
  p_org: { name: string; type: string } | null;
}

export interface SetUserConsentParams {
  p_type: 'terms_of_service' | 'privacy_policy' | 'marketing';
  p_given: boolean;
}

export interface GetUserRolesSecureParams {
  p_user_id: string;
}

export interface UserRoleRecord {
  role: string;
  organization_id: string | null;
}

export interface ArchiveShareParams {
  p_share_id: string;
}

export interface DeleteVaultDocumentParams {
  p_document_id: string;
  p_document_type: string;
  p_storage_path: string;
}

export interface DeleteVaultDocumentResult {
  success: boolean;
  message?: string;
}

export interface ManageUserRoleParams {
  p_user_id: string;
  p_organization_id: string;
  p_role: 'organization_admin' | 'parent' | 'admin';
  p_action: 'grant' | 'revoke';
}

export interface EnsureMembershipResult {
  success: boolean;
}

// Helper to call typed or untyped RPCs
// Use callRpc for typed functions, callRpcUntyped for functions not in generated types
type RpcName = keyof Database["public"]["Functions"];

function callRpc<TRpc extends RpcName>(
  name: TRpc,
  params: Database["public"]["Functions"][TRpc]["Args"]
): Promise<PostgrestSingleResponse<Database["public"]["Functions"][TRpc]["Returns"]>> {
  // Need to await the call to properly return a Promise
  return (supabase.rpc(name, params) as unknown as Promise<
    PostgrestSingleResponse<Database["public"]["Functions"][TRpc]["Returns"]>
  >);
}

// For RPCs not yet in generated types (use sparingly)
function callRpcUntyped<TReturn = unknown>(
  name: string,
  params: Record<string, unknown>
): Promise<PostgrestSingleResponse<TReturn>> {
  return (supabase.rpc(name as never, params as never) as unknown as Promise<
    PostgrestSingleResponse<TReturn>
  >);
}

/**
 * Initialize account for current user with role and optional org data
 */
export function initAccountForCurrentUser(params: InitAccountParams) {
  return callRpcUntyped('init_account_for_current_user', params as unknown as Record<string, unknown>);
}

/**
 * Set user consent for terms, privacy, or marketing
 */
export function setUserConsent(params: SetUserConsentParams) {
  return callRpc('set_user_consent', params);
}

/**
 * Get user roles securely
 */
export function getUserRolesSecure(params: GetUserRolesSecureParams) {
  return callRpc('get_user_roles_secure', params);
}

/**
 * Archive a share for parent user
 */
export function archiveShareForParent(params: ArchiveShareParams) {
  return callRpcUntyped('archive_share_for_parent', params as unknown as Record<string, unknown>);
}

/**
 * Delete a vault document with audit logging
 */
export function deleteVaultDocumentAudited(params: DeleteVaultDocumentParams) {
  return callRpc('delete_vault_document_audited', params);
}

/**
 * Manage user role (grant/revoke)
 */
export function manageUserRole(params: ManageUserRoleParams) {
  return callRpcUntyped('manage_user_role', params as unknown as Record<string, unknown>);
}

/**
 * Ensure membership for current user (self-heal)
 */
export function ensureMembershipForCurrentUser() {
  return callRpcUntyped('ensure_membership_for_current_user', {});
}

/**
 * Helper to extract error code safely from PostgrestError
 */
export function getErrorCode(error: PostgrestError | null): string | null {
  if (!error) return null;
  return error.code ?? null;
}

/**
 * Helper to extract error message safely
 */
export function getErrorMessage(error: PostgrestError | null): string {
  if (!error) return '';
  return error.message ?? '';
}
